/**
 * 风控订单/事件详情弹窗底部双按钮：与 Figma 2830:111080 / 2830:111078 一致。
 */
import React from 'react';
import { Box } from '@mui/material';
import { useTheme, type Theme } from '@mui/material/styles';
import LoadingOverlayButton from '../../component/button/loading-overlay-button.component';
import i18n from '../../i18n';
import { ReactComponent as VisibilityOffIcon } from '../../static/images/svg/material-symbols_visibility-off-rounded.svg';
import { ReactComponent as CheckCircleOutlineIcon } from '../../static/images/svg/material-symbols_check-circle-outline-rounded.svg';

export type RiskDetailDialogActionStatus = 1 | 2 | null;

export type RiskDetailDialogFooterProps = {
    /** 当前提交中的动作：1 忽略 / 2 确认；null 表示空闲 */
    actionSubmitting: RiskDetailDialogActionStatus;
    /** 点击「忽略此次风控」 */
    onIgnore: () => void;
    /** 点击「确认已处理」 */
    onConfirm: () => void;
};

/** 底部主按钮共用 sx（高度、圆角、边框与订单详情弹窗一致） */
const footerPrimaryButtonSx = (theme: Theme) => ({
    height: 56,
    px: 3,
    borderRadius: '8px',
    boxShadow: '0px 0px 4px 0px var(--overlay-800-a25)',
    textTransform: 'none' as const,
    gap: 1,
    typography: 'subtitle1',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.primary.main,
    '& .MuiButton-startIcon': { color: 'inherit' },
});

/**
 * 风控详情弹窗底部操作区：忽略 + 确认已处理。
 */
export function RiskDetailDialogFooter({
    actionSubmitting,
    onIgnore,
    onConfirm,
}: RiskDetailDialogFooterProps) {
    const theme = useTheme();
    const buttonSx = footerPrimaryButtonSx(theme);

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 2,
                width: '100%',
                flexWrap: 'wrap',
                py: 1,
                px: 2,
            }}
        >
            <LoadingOverlayButton
                type="button"
                variant="outlined"
                disableElevation
                loading={actionSubmitting === 1}
                loadingIndicatorSize={22}
                disabled={actionSubmitting !== null}
                sx={{
                    ...buttonSx,
                    color: theme.palette.text.secondary,
                    bgcolor: theme.palette.background.paper,
                    '&:hover': {
                        bgcolor: theme.palette.background.paper,
                        borderColor: theme.palette.primary.main,
                    },
                }}
                startIcon={<VisibilityOffIcon width={32} height={32} />}
                onClick={onIgnore}
            >
                {i18n.t('intelligent_risk_control_page_dialog_button_ignore_risk')}
            </LoadingOverlayButton>
            <LoadingOverlayButton
                type="button"
                variant="outlined"
                disableElevation
                loading={actionSubmitting === 2}
                loadingIndicatorSize={22}
                disabled={actionSubmitting !== null}
                sx={{
                    ...buttonSx,
                    color: theme.palette.primary.main,
                    bgcolor: 'var(--fill-primary-200)',
                    '&:hover': {
                        bgcolor: 'var(--fill-primary-200)',
                        borderColor: theme.palette.primary.main,
                    },
                }}
                startIcon={<CheckCircleOutlineIcon width={32} height={32} />}
                onClick={onConfirm}
            >
                {i18n.t('intelligent_risk_control_page_dialog_button_confirm_handled')}
            </LoadingOverlayButton>
        </Box>
    );
}
